import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RoleGuard } from '@/auth/RoleGuard';
import {
  ACCOUNTS_READ_ROLES,
  ADMISSIONS_WRITE_ROLES,
  EXAM_BOOKINGS_READ_ROLES,
  FINANCE_REPORT_ROLES,
  SALES_REPORT_ROLES,
  SALES_ROLES,
  SETTINGS_CONTENT_ROLES,
  SETTINGS_FINANCE_VIEW_ROLES,
  SETTINGS_LOOKUPS_ROLES,
  SETTINGS_USERS_ROLES,
  STUDENT_AFFAIRS_READ_ROLES,
} from '@/auth/roles';
import { CALENDAR_ROLES, FINANCE_NAV_ROLES } from '@/types/shared';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { StudentsListPage } from '@/pages/students/StudentsListPage';
import { StudentFormPage } from '@/pages/students/StudentFormPage';
import { StudentDetailPage } from '@/pages/students/StudentDetailPage';
import { StudentAffairsListPage } from '@/pages/student-affairs/StudentAffairsListPage';
import { StudentAffairsDetailPage } from '@/pages/student-affairs/StudentAffairsDetailPage';
import { AccountsListPage } from '@/pages/accounts/AccountsListPage';
import { AccountsDetailPage } from '@/pages/accounts/AccountsDetailPage';
import { ExamBookingsListPage } from '@/pages/exam-bookings/ExamBookingsListPage';
import { ExamBookingsDetailPage } from '@/pages/exam-bookings/ExamBookingsDetailPage';
import { LeadsListPage } from '@/pages/leads/LeadsListPage';
import { LeadFormPage } from '@/pages/leads/LeadFormPage';
import { LeadDetailPage } from '@/pages/leads/LeadDetailPage';
import { SalesReportsPage } from '@/pages/reports/SalesReportsPage';
import { LookupsPage } from '@/pages/settings/LookupsPage';
import { UsersPage } from '@/pages/settings/UsersPage';
import { FinanceSettingsPage } from '@/pages/settings/FinanceSettingsPage';
import { ContentSettingsPage } from '@/pages/settings/ContentSettingsPage';
import { FinancePage } from '@/pages/finance/FinancePage';
import { CostPlansPage } from '@/pages/finance/CostPlansPage';
import { FinancialReportPage } from '@/pages/finance/FinancialReportPage';
import { CalendarPage } from '@/pages/calendar/CalendarPage';

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/403', element: <ForbiddenPage /> },

  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/dashboard', element: <DashboardPage /> },
          {
            path: '/calendar',
            element: <RoleGuard roles={CALENDAR_ROLES} />,
            children: [{ index: true, element: <CalendarPage /> }],
          },
          {
            path: '/students',
            children: [
              { index: true, element: <StudentsListPage /> },
              {
                element: <RoleGuard roles={ADMISSIONS_WRITE_ROLES} />,
                children: [{ path: 'new', element: <StudentFormPage mode="create" /> }],
              },
              { path: ':studentCode', element: <StudentDetailPage /> },
              {
                element: <RoleGuard roles={ADMISSIONS_WRITE_ROLES} />,
                children: [{ path: ':studentCode/edit', element: <StudentFormPage mode="edit" /> }],
              },
            ],
          },
          {
            path: '/student-affairs',
            element: <RoleGuard roles={STUDENT_AFFAIRS_READ_ROLES} />,
            children: [
              { index: true, element: <StudentAffairsListPage /> },
              { path: ':studentCode', element: <StudentAffairsDetailPage /> },
            ],
          },
          {
            path: '/accounts',
            element: <RoleGuard roles={ACCOUNTS_READ_ROLES} />,
            children: [
              { index: true, element: <AccountsListPage /> },
              { path: ':studentCode', element: <AccountsDetailPage /> },
            ],
          },
          {
            path: '/leads',
            element: <RoleGuard roles={SALES_ROLES} />,
            children: [
              { index: true, element: <LeadsListPage /> },
              { path: 'new', element: <LeadFormPage /> },
              { path: ':id', element: <LeadDetailPage /> },
            ],
          },
          {
            path: '/reports/sales',
            element: <RoleGuard roles={SALES_REPORT_ROLES} />,
            children: [{ index: true, element: <SalesReportsPage /> }],
          },
          {
            path: '/exam-bookings',
            element: <RoleGuard roles={EXAM_BOOKINGS_READ_ROLES} />,
            children: [
              { index: true, element: <ExamBookingsListPage /> },
              { path: ':studentCode', element: <ExamBookingsDetailPage /> },
            ],
          },
          {
            path: '/finance',
            element: <RoleGuard roles={FINANCE_NAV_ROLES} />,
            children: [{ index: true, element: <FinancePage /> }],
          },
          {
            path: '/finance/cost-plans',
            element: <RoleGuard roles={FINANCE_NAV_ROLES} />,
            children: [{ index: true, element: <CostPlansPage /> }],
          },
          {
            path: '/reports/finance',
            element: <RoleGuard roles={FINANCE_REPORT_ROLES} />,
            children: [{ index: true, element: <FinancialReportPage /> }],
          },
          {
            path: '/settings/users',
            element: <RoleGuard roles={SETTINGS_USERS_ROLES} />,
            children: [{ index: true, element: <UsersPage /> }],
          },
          {
            path: '/settings/lookups',
            element: <RoleGuard roles={SETTINGS_LOOKUPS_ROLES} />,
            children: [{ index: true, element: <LookupsPage /> }],
          },
          {
            path: '/settings/finance',
            // View tier (Admin + Accounts Officer); the page's managers keep edit controls
            // Admin-only, so the accountant sees config read-only.
            element: <RoleGuard roles={SETTINGS_FINANCE_VIEW_ROLES} />,
            children: [{ index: true, element: <FinanceSettingsPage /> }],
          },
          {
            path: '/settings/content',
            element: <RoleGuard roles={SETTINGS_CONTENT_ROLES} />,
            children: [{ index: true, element: <ContentSettingsPage /> }],
          },
        ],
      },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
]);
